import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ResponsiveLayout } from '@/components/ResponsiveLayout';
import { SectionHeader } from '@/components/SectionHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  MessageCircle, Plus, Bug, Sparkles, Wrench, HelpCircle,
  Loader2, Clock, CheckCircle2, XCircle, AlertTriangle, BarChart3, MoreVertical, Trash2
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const typeConfig = {
  bug: { label: 'Bug', icon: Bug, accent: 'from-red-500 to-red-600', bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' },
  improvement: { label: 'Improvement', icon: Sparkles, accent: 'from-amber-500 to-amber-600', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  feature: { label: 'Feature', icon: Wrench, accent: 'from-blue-500 to-blue-600', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  other: { label: 'Other', icon: HelpCircle, accent: 'from-gray-500 to-gray-600', bg: 'bg-gray-500/10', text: 'text-gray-400', border: 'border-gray-500/20' }
};

const statusConfig = {
  pending: { label: 'Pending', icon: Clock, bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20' },
  in_progress: { label: 'In Progress', icon: AlertTriangle, bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/20' },
  completed: { label: 'Completed', icon: CheckCircle2, bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20' },
  rejected: { label: 'Rejected', icon: XCircle, bg: 'bg-red-500/10', text: 'text-red-400', border: 'border-red-500/20' }
};

const priorityConfig = {
  low: { label: 'Low', color: 'text-emerald-400', dot: 'bg-emerald-400' },
  medium: { label: 'Medium', color: 'text-amber-400', dot: 'bg-amber-400' },
  high: { label: 'High', color: 'text-red-400', dot: 'bg-red-400' }
};

export default function CRMFeedbackPage({ embedded = false }) {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [newFeedback, setNewFeedback] = useState({ title: '', description: '', type: 'improvement', priority: 'medium' });
  const [viewFeedback, setViewFeedback] = useState(null); // for detail popup
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = currentUser.role_id === 'admin' || currentUser.role_id === 'manager' || currentUser.role_id === 'developer';

  const handleUpdateStatus = async (feedbackId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/crm-feedback/${feedbackId}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbacks(feedbacks.map(f => f.id === feedbackId ? { ...f, status } : f));
      toast.success('Status updated');
    } catch { toast.error('Failed to update status'); }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/crm-feedback/${feedbackId}`, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbacks(feedbacks.filter(f => f.id !== feedbackId));
      toast.success('Feedback berjaya dipadam');
    } catch { toast.error('Failed to delete feedback'); }
  };

  useEffect(() => { fetchFeedbacks(); }, []);

  const fetchFeedbacks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/crm-feedback`, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbacks(response.data.feedbacks);
    } catch (error) { toast.error('Failed to load feedback'); }
    finally { setLoading(false); }
  };

  const handleAddFeedback = async () => {
    if (!newFeedback.title.trim() || !newFeedback.description.trim()) { toast.error('Please fill in all required fields'); return; }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/crm-feedback`, newFeedback, { headers: { Authorization: `Bearer ${token}` } });
      setFeedbacks([response.data.feedback, ...feedbacks]);
      setShowAddModal(false);
      setNewFeedback({ title: '', description: '', type: 'improvement', priority: 'medium' });
      toast.success('Feedback submitted successfully!');
    } catch (error) { toast.error('Failed to submit feedback'); }
    finally { setSubmitting(false); }
  };

  const filteredFeedbacks = feedbacks.filter(f => filter === 'all' || f.status === filter);

  const stats = {
    total: feedbacks.length,
    pending: feedbacks.filter(f => f.status === 'pending').length,
    in_progress: feedbacks.filter(f => f.status === 'in_progress').length,
    completed: feedbacks.filter(f => f.status === 'completed').length
  };

  if (loading) {
    if (embedded) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div>;
    return (<ResponsiveLayout><div className="flex-1 flex items-center justify-center min-h-screen bg-[hsl(var(--background))]"><Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" /></div></ResponsiveLayout>);
  }

  const addBtn = <Button data-testid="add-feedback-btn" onClick={() => setShowAddModal(true)} className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white shadow-lg shadow-blue-500/20"><Plus size={18} className="mr-2" /> Submit Feedback</Button>;

  const content = (
    <>
      {!embedded && (
        <SectionHeader icon={MessageCircle} title="CRM Feedback" subtitle="Suggestions & reports for CRM improvements" iconBg="bg-gradient-to-br from-blue-500 to-cyan-600 shadow-blue-500/20" action={addBtn} />
      )}
      {embedded && <div className="flex justify-end mb-4">{addBtn}</div>}

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'All', value: stats.total, accent: 'border-[hsl(var(--border))]', vc: '' },
            { label: 'Pending', value: stats.pending, accent: 'border-amber-500/30', vc: 'text-amber-400' },
            { label: 'In Progress', value: stats.in_progress, accent: 'border-blue-500/30', vc: 'text-blue-400' },
            { label: 'Completed', value: stats.completed, accent: 'border-emerald-500/30', vc: 'text-emerald-400' },
          ].map((stat) => (
            <div key={stat.label} className={`bg-[hsl(var(--card))] border ${stat.accent} rounded-xl p-4`}>
              <p className={`text-xs ${stat.vc || 'text-[hsl(var(--text-muted))]'} mb-1`}>{stat.label}</p>
              <p className={`text-2xl font-bold ${stat.vc}`}>{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
          {['all', 'pending', 'in_progress', 'completed', 'rejected'].map((f) => (
            <Button key={f} variant={filter === f ? 'default' : 'outline'} size="sm" onClick={() => setFilter(f)} className={filter === f ? 'bg-gradient-to-r from-blue-500 to-cyan-600 text-white border-0' : 'border-[hsl(var(--border))] text-[hsl(var(--text-secondary))]'}>
              {f === 'all' ? 'All' : statusConfig[f]?.label || f}
            </Button>
          ))}
        </div>

        {/* Feedback List */}
        <div className="space-y-3">
          {filteredFeedbacks.length === 0 ? (
            <div className="text-center py-16 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl">
              <MessageCircle size={48} className="mx-auto text-blue-500/30 mb-4" />
              <p className="text-[hsl(var(--text-secondary))] font-medium">No feedback found</p>
            </div>
          ) : (
            filteredFeedbacks.map((feedback) => {
              const type = typeConfig[feedback.type] || typeConfig.other;
              const status = statusConfig[feedback.status] || statusConfig.pending;
              const priority = priorityConfig[feedback.priority] || priorityConfig.medium;
              const TypeIcon = type.icon;
              const StatusIcon = status.icon;
              
              return (
                <div
                  key={feedback.id}
                  data-testid={`crm-feedback-${feedback.id}`}
                  onClick={() => setViewFeedback(feedback)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setViewFeedback(feedback); } }}
                  role="button"
                  tabIndex={0}
                  className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 hover:border-blue-500/40 hover:bg-[hsl(var(--surface-elevated))]/30 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${type.bg} ${type.text} border ${type.border} text-xs`}>
                        <TypeIcon size={10} className="mr-1" />{type.label}
                      </Badge>
                      <Badge className={`${status.bg} ${status.text} border ${status.border} text-xs`}>
                        <StatusIcon size={10} className="mr-1" />{status.label}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <span className={`text-xs font-semibold flex items-center gap-1.5 ${priority.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${priority.dot}`} />
                        {priority.label}
                      </span>
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreVertical size={14} /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                            <DropdownMenuItem onClick={() => handleUpdateStatus(feedback.id, 'in_progress')}><AlertTriangle size={14} className="mr-2 text-blue-400" /> In Progress</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(feedback.id, 'completed')}><CheckCircle2 size={14} className="mr-2 text-emerald-400" /> Completed</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleUpdateStatus(feedback.id, 'rejected')}><XCircle size={14} className="mr-2 text-red-400" /> Rejected</DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDeleteFeedback(feedback.id)} className="text-red-400" data-testid={`delete-feedback-${feedback.id}`}><Trash2 size={14} className="mr-2" /> Delete</DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold text-[hsl(var(--text-primary))] mb-1.5">{feedback.title}</h3>
                  <p className="text-sm text-[hsl(var(--text-muted))] mb-3 line-clamp-2">{feedback.description}</p>
                  {feedback.description && feedback.description.length > 120 && (
                    <span className="text-xs text-blue-400 font-medium hover:underline">Click to read more →</span>
                  )}
                  {feedback.admin_notes && (
                    <div className="bg-[hsl(var(--surface))] rounded-lg p-3 mb-3 border-l-2 border-blue-500/50 mt-3">
                      <p className="text-xs text-blue-400 font-medium mb-1">Admin Notes</p>
                      <p className="text-sm text-[hsl(var(--text-secondary))] line-clamp-2">{feedback.admin_notes}</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between text-xs text-[hsl(var(--text-muted))] pt-3 border-t border-[hsl(var(--border))] mt-3">
                    <span>{feedback.user_name}</span>
                    <span>{new Date(feedback.created_at).toLocaleDateString('ms-MY')}</span>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Add Feedback Modal */}
        <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-md">
            <DialogHeader>
              <DialogTitle className="text-[hsl(var(--text-primary))] flex items-center gap-2">
                <MessageCircle size={18} className="text-blue-400" /> Submit CRM Feedback
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Title *</label>
                <Input data-testid="feedback-title-input" value={newFeedback.title} onChange={(e) => setNewFeedback({ ...newFeedback, title: e.target.value })} placeholder="Feedback title" className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]" />
              </div>
              <div>
                <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Description *</label>
                <textarea data-testid="feedback-desc-input" value={newFeedback.description} onChange={(e) => setNewFeedback({ ...newFeedback, description: e.target.value })} placeholder="Describe your feedback..." rows={4} className="w-full bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-md p-3 text-sm text-[hsl(var(--text-primary))] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500/30" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Type</label>
                  <Select value={newFeedback.type} onValueChange={(v) => setNewFeedback({ ...newFeedback, type: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="bug">Bug Report</SelectItem>
                      <SelectItem value="improvement">Improvement</SelectItem>
                      <SelectItem value="feature">Feature Request</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-[hsl(var(--text-secondary))] mb-1.5 block font-medium">Priority</label>
                  <Select value={newFeedback.priority} onValueChange={(v) => setNewFeedback({ ...newFeedback, priority: v })}>
                    <SelectTrigger className="bg-[hsl(var(--surface))] border-[hsl(var(--border))]"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
              <Button data-testid="submit-feedback-btn" onClick={handleAddFeedback} disabled={submitting} className="bg-gradient-to-r from-blue-500 to-cyan-600 text-white">
                {submitting ? <Loader2 size={16} className="animate-spin mr-2" /> : null} Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail View Modal — click on a feedback card to see full content */}
        <Dialog open={!!viewFeedback} onOpenChange={(open) => { if (!open) setViewFeedback(null); }}>
          <DialogContent className="bg-[hsl(var(--card))] border-[hsl(var(--border))] max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="feedback-detail-modal">
            {viewFeedback && (() => {
              const t = typeConfig[viewFeedback.type] || typeConfig.other;
              const s = statusConfig[viewFeedback.status] || statusConfig.pending;
              const p = priorityConfig[viewFeedback.priority] || priorityConfig.medium;
              const TI = t.icon;
              const SI = s.icon;
              return (
                <>
                  <DialogHeader>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className={`${t.bg} ${t.text} border ${t.border} text-xs`}><TI size={10} className="mr-1" />{t.label}</Badge>
                      <Badge className={`${s.bg} ${s.text} border ${s.border} text-xs`}><SI size={10} className="mr-1" />{s.label}</Badge>
                      <span className={`text-xs font-semibold flex items-center gap-1.5 ${p.color}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${p.dot}`} />
                        {p.label} priority
                      </span>
                    </div>
                    <DialogTitle className="text-[hsl(var(--text-primary))] text-lg leading-snug" data-testid="feedback-detail-title">
                      {viewFeedback.title}
                    </DialogTitle>
                  </DialogHeader>

                  <div className="space-y-4 pt-2">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-[hsl(var(--text-muted))] mb-1.5">Description</p>
                      <p className="text-sm text-[hsl(var(--text-secondary))] whitespace-pre-wrap leading-relaxed" data-testid="feedback-detail-description">
                        {viewFeedback.description || <span className="italic opacity-60">No description provided.</span>}
                      </p>
                    </div>

                    {viewFeedback.admin_notes && (
                      <div className="bg-[hsl(var(--surface))] rounded-lg p-3 border-l-2 border-blue-500/50">
                        <p className="text-xs text-blue-400 font-medium mb-1.5">Admin Notes</p>
                        <p className="text-sm text-[hsl(var(--text-secondary))] whitespace-pre-wrap" data-testid="feedback-detail-admin-notes">
                          {viewFeedback.admin_notes}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <p className="text-[hsl(var(--text-muted))] mb-1">Submitted by</p>
                        <p className="text-[hsl(var(--text-primary))] font-medium">{viewFeedback.user_name || '—'}</p>
                      </div>
                      <div>
                        <p className="text-[hsl(var(--text-muted))] mb-1">Submitted on</p>
                        <p className="text-[hsl(var(--text-primary))] font-medium">
                          {viewFeedback.created_at ? new Date(viewFeedback.created_at).toLocaleString('ms-MY') : '—'}
                        </p>
                      </div>
                      {viewFeedback.updated_at && (
                        <div>
                          <p className="text-[hsl(var(--text-muted))] mb-1">Last updated</p>
                          <p className="text-[hsl(var(--text-primary))] font-medium">
                            {new Date(viewFeedback.updated_at).toLocaleString('ms-MY')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  <DialogFooter className="flex-wrap gap-2 pt-4 border-t border-[hsl(var(--border))]">
                    {isAdmin && viewFeedback.status !== 'in_progress' && (
                      <Button variant="outline" size="sm" onClick={() => { handleUpdateStatus(viewFeedback.id, 'in_progress'); setViewFeedback({ ...viewFeedback, status: 'in_progress' }); }} className="border-blue-500/40 text-blue-300" data-testid="detail-action-in-progress">
                        <AlertTriangle size={12} className="mr-1.5" /> Mark In Progress
                      </Button>
                    )}
                    {isAdmin && viewFeedback.status !== 'completed' && (
                      <Button variant="outline" size="sm" onClick={() => { handleUpdateStatus(viewFeedback.id, 'completed'); setViewFeedback({ ...viewFeedback, status: 'completed' }); }} className="border-emerald-500/40 text-emerald-300" data-testid="detail-action-completed">
                        <CheckCircle2 size={12} className="mr-1.5" /> Mark Completed
                      </Button>
                    )}
                    {isAdmin && viewFeedback.status !== 'rejected' && (
                      <Button variant="outline" size="sm" onClick={() => { handleUpdateStatus(viewFeedback.id, 'rejected'); setViewFeedback({ ...viewFeedback, status: 'rejected' }); }} className="border-red-500/40 text-red-300" data-testid="detail-action-rejected">
                        <XCircle size={12} className="mr-1.5" /> Reject
                      </Button>
                    )}
                    <Button onClick={() => setViewFeedback(null)} data-testid="detail-close-btn">Close</Button>
                  </DialogFooter>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </>
    );

  if (embedded) return content;

  return (
    <ResponsiveLayout>
      <div className="flex-1 min-h-screen bg-[hsl(var(--background))] text-[hsl(var(--text-primary))] p-4 lg:p-6">
        {content}
      </div>
    </ResponsiveLayout>
  );
}
